import _set from 'lodash.set'
import { getConditionTypes } from '../condition/conditions'
import Indicator from '../condition/indicators/types/Indicator'
// Direct file import (not the composite barrel) to avoid an import cycle
// through validateComposite
import { resolveSideIndicator } from '../../../common/utils/composite/registryEntry'

export type IndicatorLookup =
  | ((value: string | null | undefined) => Indicator | undefined)
  | undefined

/**
 * Normalizes a condition per its condition-type rules (threshold/RHS/lookback
 * clearing) and stamps `condition.isValid`. MUTATES the passed condition.
 * Extracted verbatim from PatternTreeContext.handleChangeCondition so the
 * copilot's draft normalization applies the identical rules to agent-built
 * conditions.
 */
export const normalizeConditionValidity = (
  updatedCondition: any,
  getIndicatorByValue: IndicatorLookup
): void => {
  // Validate the condition. Resolution falls back to the side's embedded
  // composite snapshot so conditions referencing indicators the current user
  // doesn't own (shared/copied patterns) stay valid and renderable.
  const LHS = resolveSideIndicator(updatedCondition.condition.LHS, getIndicatorByValue)
  const conditionType = getConditionTypes(updatedCondition.condition.LHS?.field)?.find(
    (c: any) => c.value === updatedCondition.condition.conditionType
  )

  let isValid = false
  if (LHS && conditionType) {
    if (conditionType.hasRHS) {
      isValid = !!(
        resolveSideIndicator(updatedCondition.condition.RHS, getIndicatorByValue) ||
        updatedCondition.condition.RHS?.indicator?.name === 'value'
      )
    } else {
      isValid = true
    }

    // Legacy CHANGED_* conditions without threshold are invalid
    if (conditionType.hasThreshold && updatedCondition.condition.threshold === undefined) {
      isValid = false
    }
  }

  // Clear threshold if condition type doesn't support it
  if (conditionType && !conditionType.hasThreshold) {
    delete updatedCondition.condition.threshold
  }

  // Clear RHS if condition type has threshold and RHS is value (not allowed)
  if (conditionType?.hasThreshold && updatedCondition.condition.RHS?.indicator?.name === 'value') {
    delete updatedCondition.condition.RHS
  }

  // Clear LHS candle lookback when condition type has RHS (range only allowed without RHS)
  if (conditionType?.hasRHS && updatedCondition.condition.LHS?.candle?.lookback) {
    delete updatedCondition.condition.LHS.candle.lookback
    updatedCondition.condition.LHS.candle.offset = 0
  }

  // Set the validity status
  _set(updatedCondition, 'condition.isValid', isValid)
}
